import React, { useState, useEffect } from 'react';
import {
    Box,
    IconButton,
    Typography,
    Popover,
    TextField,
    InputAdornment,
} from '@mui/material';
import {
    ArrowForward as ArrowForwardIcon,
    ArrowBack as ArrowBackIcon,
    CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { toJalaali, toGregorian } from 'jalaali-js';
import { toPersianNumber } from '../utils/persianNumber';

interface PersianDatePickerProps {
    value: string; // YYYY-MM-DD
    onChange: (date: string) => void;
    label?: string;
    minDate?: string | Date;
    maxDate?: string | Date;
}

const weekDays = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];
const monthNames = [
    'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
    'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'
];

const PersianDatePicker: React.FC<PersianDatePickerProps> = ({ value, onChange, label = 'تاریخ', minDate, maxDate }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [currentMonth, setCurrentMonth] = useState(new Date(value));

    useEffect(() => {
        if (value) {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
                setCurrentMonth(date);
            }
        }
    }, [value]);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setCurrentMonth(new Date(value || new Date()));
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const navigateMonth = (direction: 'prev' | 'next') => {
        setCurrentMonth((prev) => {
            // Convert to Jalaali to navigate months correctly in Persian calendar
            const jDate = toJalaali(prev.getFullYear(), prev.getMonth() + 1, prev.getDate());
            let jy = jDate.jy;
            let jm = jDate.jm;

            if (direction === 'prev') {
                jm--;
                if (jm < 1) {
                    jm = 12;
                    jy--;
                }
            } else {
                jm++;
                if (jm > 12) {
                    jm = 1;
                    jy++;
                }
            }

            // Convert back to Gregorian to set state (using 1st day of month)
            const gDate = toGregorian(jy, jm, 1);
            return new Date(gDate.gy, gDate.gm - 1, gDate.gd);
        });
    };

    const getDaysInMonth = () => {
        const jDate = toJalaali(currentMonth.getFullYear(), currentMonth.getMonth() + 1, currentMonth.getDate());
        const jy = jDate.jy;
        const jm = jDate.jm;

        // First day of Persian month
        const firstDayG = toGregorian(jy, jm, 1);
        const firstDayDate = new Date(firstDayG.gy, firstDayG.gm - 1, firstDayG.gd);

        // Day of week (Saturday=0 in our grid, but Date.getDay() returns Sun=0, Sat=6)
        // We want Sat=0, Sun=1, ... Fri=6
        // JS: Sun=0, Mon=1, Tue=2, Wed=3, Thu=4, Fri=5, Sat=6
        // Target: Sat=0, Sun=1...
        const startDayOfWeek = (firstDayDate.getDay() + 1) % 7;

        const daysInMonth = jm <= 6 ? 31 : (jm <= 11 ? 30 : (isLeapJalaaliYear(jy) ? 30 : 29));

        const days = [];
        // Empty slots
        for (let i = 0; i < startDayOfWeek; i++) {
            days.push(null);
        }
        // Days
        for (let i = 1; i <= daysInMonth; i++) {
            const gDate = toGregorian(jy, jm, i);
            days.push({
                day: i,
                dateStr: `${gDate.gy}-${String(gDate.gm).padStart(2, '0')}-${String(gDate.gd).padStart(2, '0')}`
            });
        }

        return { days, jy, jm };
    };

    // Helper to check leap year (simplified or use library if available)
    const isLeapJalaaliYear = (jy: number) => {
        // jalaali-js doesn't export isLeapJalaaliYear in types usually, but let's assume it exists or use logic
        // Logic: (year + 2346) % 128 < 30 roughly?
        // Better: use jalaali-js if possible. imported above? No.
        // Let's copy logic from DailyMealPlanning or assume simplistic.
        // Actually `jalaali-js` exports `jalaaliMonthLength`!
        // But I don't see it imported. Let's stick to standard logic.
        return ((jy + 2346) % 128) < 30; // Close approximation
    };

    const { days, jy, jm } = getDaysInMonth();

    // Format formatted value for display
    const jValue = value ? toJalaali(new Date(value).getFullYear(), new Date(value).getMonth() + 1, new Date(value).getDate()) : null;
    const displayValue = jValue
        ? `${toPersianNumber(jValue.jy)}/${toPersianNumber(jValue.jm)}/${toPersianNumber(jValue.jd)}`
        : '';

    return (
        <Box>
            <TextField
                value={displayValue}
                onClick={handleOpen}
                placeholder="تاریخ را انتخاب کنید"
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton onClick={handleOpen} edge="end" size="small">
                                <CalendarIcon />
                            </IconButton>
                        </InputAdornment>
                    ),
                    sx: { fontFamily: 'Vazir, sans-serif', cursor: 'pointer' }
                }}
                inputProps={{
                    sx: { cursor: 'pointer', fontFamily: 'Vazir, sans-serif' }
                }}
                fullWidth
            />

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <Box sx={{ p: 2, width: 300, dir: 'rtl' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <IconButton onClick={() => navigateMonth('prev')} size="small">
                            <ArrowForwardIcon fontSize="small" />
                        </IconButton>
                        <Typography sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                            {monthNames[jm - 1]} {toPersianNumber(jy)}
                        </Typography>
                        <IconButton onClick={() => navigateMonth('next')} size="small">
                            <ArrowBackIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
                        {weekDays.map(d => (
                            <Typography key={d} align="center" variant="caption" color="text.secondary" sx={{ fontFamily: 'Vazir, sans-serif' }}>
                                {d}
                            </Typography>
                        ))}
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                        {days.map((d, i) => {
                            if (!d) return <Box key={i} />;
                            const isSelected = value === d.dateStr;
                            const isToday = d.dateStr === new Date().toISOString().split('T')[0];
                            
                            let isDisabled = false;
                            if (minDate) {
                                const minDateObj = new Date(minDate);
                                minDateObj.setHours(0, 0, 0, 0);
                                const dDateObj = new Date(d.dateStr);
                                dDateObj.setHours(0, 0, 0, 0);
                                if (dDateObj < minDateObj) {
                                    isDisabled = true;
                                }
                            }
                            if (maxDate) {
                                const maxDateObj = new Date(maxDate);
                                maxDateObj.setHours(0, 0, 0, 0);
                                const dDateObj = new Date(d.dateStr);
                                dDateObj.setHours(0, 0, 0, 0);
                                if (dDateObj > maxDateObj) {
                                    isDisabled = true;
                                }
                            }

                            return (
                                <Box
                                    key={i}
                                    onClick={() => {
                                        if (isDisabled) return;
                                        onChange(d.dateStr);
                                        handleClose();
                                    }}
                                    sx={{
                                        height: 32,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                                        borderRadius: '50%',
                                        bgcolor: isSelected ? 'primary.main' : 'transparent',
                                        color: isDisabled ? 'text.disabled' : (isSelected ? 'white' : (isToday ? 'primary.main' : 'text.primary')),
                                        border: isToday && !isSelected && !isDisabled ? '1px solid' : 'none',
                                        borderColor: 'primary.main',
                                        opacity: isDisabled ? 0.5 : 1,
                                        '&:hover': {
                                            bgcolor: isDisabled ? 'transparent' : (isSelected ? 'primary.dark' : 'rgba(91, 124, 250, 0.1)'),
                                        }
                                    }}
                                >
                                    <Typography variant="body2" sx={{ fontFamily: 'Vazir, sans-serif' }}>
                                        {toPersianNumber(d.day)}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Popover>
        </Box>
    );
};

export default PersianDatePicker;
